/** @jsx jsx */
import { jsx } from "@emotion/core"
import { Button, Spacer } from "@cd2/components"
import { H2, P } from "@cd2/text"
import React, { useState } from "react"
import { useAsyncCallback } from "./useAsyncCallback"
import { useDialogueConfirmation2 } from "./useDialogConfirmation"
import { CancelButton } from "../interfaceParts/CancelButton"
import { Flex } from "../interfaceParts/Flex"
import { NewCompanySelectField } from "../forms/parts/CompanySelectField"
import { useGlobalState } from "../utils/globalState"
import { useEvents } from "../utils/useEvents"
import { useSiteAlerts } from "./useSiteAlerts"
import { icons } from "../utils/icons"
import { IRecord } from "../types/schema"
import { LoadingFrame } from "../pages/user/UserResultsShowPage/AssessmentDetailsTab"

export function canChangeCaseCompany(globalState: ReturnType<typeof useGlobalState>) {
  return globalState.accessibleCompanyIds.length > 1 && !globalState.user.isReadonly
}

export function useChangeCaseCompanyDialog() {
  return useDialogueConfirmation2<
    boolean,
    { resultId: string; companyId: string; companyName: string }
  >({
    Component({ dialogData, onResolve, registerOutsideClick }) {
      registerOutsideClick(() => onResolve(false))
      const globalState = useGlobalState()
      const events = useEvents()
      const alerts = useSiteAlerts()
      const [companyId, setCompanyId] = useState(dialogData.companyId)
      const [loaded, setLoaded] = useState(false)

      React.useEffect(() => {
        setCompanyId(dialogData.companyId)
        setLoaded(true)
      }, [dialogData.companyId, dialogData.resultId])

      const handleSubmit = useAsyncCallback(async () => {
        if (companyId === dialogData.companyId) {
          onResolve(false)
          return
        }

        const newCompany = await window.skeem.fetch2<IRecord<"companies", "name"> | null>({
          first: {
            name: "companies",
            attributes: ["name"],
            matches: { id: companyId },
          },
        })

        await window.skeem.mutate({
          flowResults: {
            update: {
              id: dialogData.resultId,
              company: { set: companyId },
              employees: { set: [] },
              location: { set: null },
              updatedAt: new Date(),
            },
          },
        })

        await events.updateRecord("results", dialogData.resultId, {
          company: { was: dialogData.companyName, now: newCompany?.name ?? companyId },
        })

        alerts.genericSuccess("Company updated!")
        onResolve(true)
      })

      if (!loaded) {
        return <LoadingFrame />
      }

      return (
        <div>
          <H2>Change Company</H2>
          <P>
            Change which company this case belongs to. Employees and location will be cleared when
            the company is changed.
          </P>
          <NewCompanySelectField
            label="Company"
            value={companyId}
            onChange={company => company && setCompanyId(company.id)}
            skeemFilter={{ matches: { id: globalState.accessibleCompanyIds } }}
          />
          <Spacer />
          <Flex gap={4}>
            <Button leftIcon={icons.save} onClick={handleSubmit}>
              Save
            </Button>
            <CancelButton onClick={() => onResolve(false)} />
          </Flex>
        </div>
      )
    },
  })
}
